import { useEffect, useState } from 'react';
import { CrmStats } from './types';
import { getCrmStats, getErrorMessage } from './adminApi';
import { formatDuration, formatPercent, roleLabel } from './crmShared';
import CallsPerDayChart from './CallsPerDayChart';

type Period = 'today' | '7d';

export default function PerformancePage() {
  const [period, setPeriod] = useState<Period>('today');
  const [stats, setStats] = useState<CrmStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    getCrmStats(period)
      .then((data) => { if (mounted) setStats(data); })
      .catch((err) => { if (mounted) setError(getErrorMessage(err)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [period]);

  const totals = stats?.totals;

  return (
    <div className="px-4 py-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-950">Performance des appels</h2>
          <p className="mt-1 text-sm text-gray-500">Activité de l’équipe de téléconseil</p>
        </div>
        <div className="inline-flex rounded-md border border-gray-300 bg-white p-1 text-sm font-medium">
          {([
            ['today', "Aujourd'hui"],
            ['7d', '7 derniers jours'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setPeriod(value)}
              className={`rounded px-4 py-1.5 transition-colors ${
                period === value ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {totals && (
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { label: 'Appels', value: totals.volume },
            { label: 'Taux de contact', value: formatPercent(totals.contactRate) },
            { label: 'RDV pris', value: totals.rdvPris },
            { label: 'Vitesse 1er contact', value: formatDuration(totals.firstContactSpeedMs) },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm">
              <p className="text-2xl font-bold text-gray-950">{value}</p>
              <p className="text-sm text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      <CallsPerDayChart />

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Appelant</th>
                <th className="px-4 py-3 text-right">Appels</th>
                <th className="px-4 py-3 text-right">Contactés</th>
                <th className="px-4 py-3 text-right">Taux contact</th>
                <th className="px-4 py-3 text-right">RDV pris</th>
                <th className="px-4 py-3 text-right">Conversion</th>
                <th className="px-4 py-3 text-right">Vitesse 1er contact</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">Chargement…</td>
                </tr>
              ) : !stats || stats.rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">Aucune activité sur la période.</td>
                </tr>
              ) : (
                stats.rows.map((row) => (
                  <tr key={row.callerId} className={row.isActive ? '' : 'opacity-60'}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-950">{row.name ?? row.email}</div>
                      <div className="text-xs text-gray-500">
                        {roleLabel(row.role)}
                        {!row.isActive ? ' · désactivé' : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{row.volume}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{row.joints}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{formatPercent(row.contactRate)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{row.rdvPris}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{formatPercent(row.conversionRate)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{formatDuration(row.firstContactSpeedMs)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
